import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { api, formatCurrency } from "@/lib/api";
import { logger } from "@/lib/logger";
import { Wallet, Search, X, Home, Link2Off } from "lucide-react";
import { toast } from "sonner";

const CATEGORY_ICONS = {
  nomina: "💼", hipoteca: "🏠", alquiler: "🏠", servicios: "💡",
  supermercado: "🛒", restaurantes: "🍽️", transporte: "🚗", gasolina: "⛽",
  salud: "🏥", ocio: "🎉", ropa: "👕", viaje: "✈️", otros: "•",
};

const INITIAL_LIMIT = 500;
const RENDER_CAP = 200;

/** Compact bottom-sheet style dialog showing recent transactions of one account with search. */
export default function AccountTxSheet({ open, onOpenChange, account }) {
  const [txs, setTxs] = useState(null);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [properties, setProperties] = useState([]);
  // { [tx_id]: linked_property_id | "" }
  const [linkingId, setLinkingId] = useState(null);

  useEffect(() => {
    if (!open || !account?.id) return;
    setQuery("");
    setLoading(true);
    Promise.all([
      api.get(`/accounts/${account.id}/transactions?limit=${INITIAL_LIMIT}`),
      api.get(`/investments`),
    ])
      .then(([tx, inv]) => {
        setTxs(tx.data || []);
        setProperties((inv.data || []).filter((i) => i.type === "real_estate"));
      })
      .catch((e) => { logger.error("acc tx sheet", e); setTxs([]); })
      .finally(() => setLoading(false));
  }, [open, account?.id]);

  const linkProperty = async (txId, propertyId) => {
    setLinkingId(txId);
    try {
      await api.patch(`/transactions/${txId}`, { linked_property_id: propertyId });
      setTxs((prev) => (prev || []).map((t) => (
        t.id === txId ? { ...t, linked_property_id: propertyId || null } : t
      )));
      toast.success(propertyId ? "Vinculado a inmueble" : "Vínculo eliminado");
    } catch {
      toast.error("No se pudo vincular");
    } finally {
      setLinkingId(null);
    }
  };

  const filtered = useMemo(() => {
    if (!txs) return [];
    const q = query.trim().toLowerCase();
    if (!q) return txs;
    return txs.filter((t) => {
      const desc = (t.description || "").toLowerCase();
      const cat = (t.category || "").toLowerCase();
      return desc.includes(q) || cat.includes(q);
    });
  }, [txs, query]);

  const displayed = filtered.slice(0, RENDER_CAP);
  const total = txs ? txs.length : 0;
  const shown = filtered.length;

  if (!account) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-white p-0 overflow-hidden">
        <div className="p-6 pb-4">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <Wallet className="w-5 h-5 text-[#1C7C54]" />
              {account.alias || account.bank}
            </DialogTitle>
          </DialogHeader>
          <div className="mt-2 flex items-baseline gap-2">
            <div className="font-display text-3xl font-semibold tabular-nums text-[#0A110E]">
              {formatCurrency(account.balance || 0, account.currency)}
            </div>
            <div className="text-xs text-[#8A9992] uppercase tracking-[0.1em]">
              {account.bank}
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="px-4 pb-3">
          <div className="relative">
            <Search className="w-4 h-4 text-[#8A9992] absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              type="text"
              data-testid="acc-tx-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar por concepto o categoría…"
              className="pl-9 pr-9 h-10"
            />
            {query && (
              <button
                type="button"
                data-testid="acc-tx-search-clear"
                onClick={() => setQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-[#EBEBEA] text-[#8A9992]"
                aria-label="Limpiar búsqueda"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>

        <div className="border-t border-[#EBEBEA] bg-[#FAFAF9] px-4 py-2 text-[10px] uppercase tracking-[0.14em] font-bold text-[#8A9992] flex items-center justify-between">
          <span>Movimientos</span>
          <span data-testid="acc-tx-count" className="tabular-nums text-[#5F7368] normal-case tracking-normal font-medium">
            {query ? `${shown} de ${total}` : `${Math.min(total, RENDER_CAP)} de ${total}`}
          </span>
        </div>

        <div
          data-testid="acc-tx-sheet-list"
          className="max-h-[55vh] overflow-y-auto divide-y divide-[#EBEBEA]"
        >
          {loading ? (
            <div className="p-6 text-sm text-[#8A9992]">Cargando…</div>
          ) : displayed.length === 0 ? (
            <div className="p-6 text-sm text-[#8A9992]">
              {query
                ? `Ningún movimiento coincide con “${query}”.`
                : "Aún no hay movimientos en esta cuenta. Importa un extracto para verlos aquí."}
            </div>
          ) : (
            displayed.map((t, i) => {
              const positive = (t.amount || 0) >= 0;
              const linkedProp = properties.find((p) => p.id === t.linked_property_id);
              const canLink = !positive && properties.length > 0;
              return (
                <div
                  key={t.id || i}
                  data-testid={`acc-tx-row-${i}`}
                  className="flex items-start gap-3 px-4 py-3"
                >
                  <span className="text-xl leading-none mt-0.5">
                    {CATEGORY_ICONS[t.category] || (positive ? "⬆︎" : "⬇︎")}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-[#0A110E] truncate">
                      {t.description || t.category || "Movimiento"}
                    </div>
                    <div className="text-[11px] text-[#8A9992] flex flex-wrap items-center gap-x-1">
                      <span>{(t.date || "").slice(0, 10)}</span>
                      {t.category && <span>· {t.category}</span>}
                      {linkedProp && (
                        <span
                          data-testid={`tx-linked-${i}`}
                          className="inline-flex items-center gap-1 h-5 px-1.5 rounded-full bg-[#1C7C54]/10 text-[#1C7C54] font-semibold"
                        >
                          <Home className="w-2.5 h-2.5" /> {linkedProp.name}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <div
                      className={`text-sm font-semibold tabular-nums whitespace-nowrap ${
                        positive ? "text-[#1C7C54]" : "text-[#0A110E]"
                      }`}
                    >
                      {positive ? "+" : ""}{formatCurrency(t.amount || 0, account.currency)}
                    </div>
                    {canLink && (
                      <div className="flex items-center gap-1">
                        <select
                          data-testid={`tx-link-select-${i}`}
                          disabled={linkingId === t.id}
                          value={t.linked_property_id || ""}
                          onChange={(e) => linkProperty(t.id, e.target.value)}
                          className="h-6 max-w-[130px] text-[10px] rounded-md border border-[#EBEBEA] bg-white text-[#5F7368] focus:outline-none focus:border-[#1C7C54]/60 disabled:opacity-50"
                        >
                          <option value="">🏠 Vincular…</option>
                          {properties.map((p) => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                          ))}
                        </select>
                        {t.linked_property_id && (
                          <button
                            type="button"
                            title="Quitar vínculo"
                            data-testid={`tx-unlink-${i}`}
                            onClick={() => linkProperty(t.id, "")}
                            className="p-1 rounded hover:bg-[#EBEBEA] text-[#8A9992]"
                          >
                            <Link2Off className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
          {filtered.length > RENDER_CAP && (
            <div className="px-4 py-3 text-xs text-[#8A9992] text-center bg-[#FAFAF9]">
              Mostrando primeros {RENDER_CAP} de {shown}. Refina la búsqueda para ver más.
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
